import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { EmailCodeAction, verifyConfirmEmail } from '@/models/confirmInfo'
import type { EditedCard } from './confirmSteps'
import { useConfirmInfoStep } from './useConfirmInfoStep'
import { useSaveEdits } from './useSaveEdits'

const CODE_LENGTH = 6

interface VerifyEmailCardProps {
  orgId: string
  email: string
  /** Confirming in this browser just emailed one; a teammate arriving at this step asks for theirs. */
  codeSent: boolean
  /** The code checking out asks the carriers, so edits save first. */
  edited: EditedCard[]
}

/** The chat's email check: a link signed them in, which proves nothing about the address. */
export function VerifyEmailCard({ orgId, email, codeSent, edited }: VerifyEmailCardProps) {
  const [sent, setSent] = useState(codeSent)
  const [code, setCode] = useState('')
  const [editing, setEditing] = useState(false)
  const [newEmail, setNewEmail] = useState(email)
  const { saveEdits, saving } = useSaveEdits(edited)
  const { run, pending, error } = useConfirmInfoStep(orgId)
  const busy = saving || pending

  async function submit(value: string) {
    if (!(await saveEdits())) return
    if (!(await run(() => verifyConfirmEmail(orgId, { code: value })))) setCode('')
  }

  async function sendCode() {
    setCode('')
    if (await run(() => verifyConfirmEmail(orgId, { action: EmailCodeAction.RESEND }))) setSent(true)
  }

  async function changeEmail(event: React.FormEvent) {
    event.preventDefault()
    const ok = await run(() =>
      verifyConfirmEmail(orgId, { action: EmailCodeAction.CHANGE_EMAIL, email: newEmail.trim() })
    )
    if (ok) {
      setEditing(false)
      setSent(true)
      setCode('')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Confirm your email</CardTitle>
        <CardDescription>
          {sent ? 'We sent a 6-digit code to' : "We'll email a 6-digit code to"} {email}. Enter it so your
          quotes and paperwork reach you.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {editing ? (
          <form onSubmit={(event) => void changeEmail(event)} className="flex max-w-sm flex-col gap-3">
            <Field>
              <FieldLabel htmlFor="confirm-new-email">Email address</FieldLabel>
              <Input
                id="confirm-new-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoFocus
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!newEmail.trim()} loading={pending ? 'Sending…' : undefined}>
                Send the code there
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : !sent ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={() => void sendCode()} loading={pending ? 'Sending…' : undefined}>
              Send code
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(true)} disabled={pending}>
              Use a different email
            </Button>
          </div>
        ) : (
          <>
            <InputOTP
              maxLength={CODE_LENGTH}
              value={code}
              onChange={(value) => {
                setCode(value)
                if (value.length === CODE_LENGTH) void submit(value)
              }}
              autoComplete="one-time-code"
              disabled={busy}
              aria-label="6-digit code"
            >
              <InputOTPGroup>
                {Array.from({ length: CODE_LENGTH }).map((_, index) => (
                  <InputOTPSlot key={index} index={index} className="border" />
                ))}
              </InputOTPGroup>
            </InputOTP>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={() => void submit(code)}
                disabled={code.length < CODE_LENGTH}
                loading={saving ? 'Saving…' : pending ? 'Checking…' : undefined}
              >
                Confirm email
              </Button>
              <Button type="button" variant="ghost" onClick={() => void sendCode()} disabled={busy}>
                Send a new code
              </Button>
              <Button type="button" variant="ghost" onClick={() => setEditing(true)} disabled={busy}>
                Use a different email
              </Button>
            </div>
          </>
        )}
        <FieldError className="text-xs">{error}</FieldError>
      </CardContent>
    </Card>
  )
}
